import React, { useEffect, useState } from "react";
import { User } from "../../../core/types/types";
import { useSelector } from "react-redux";
import apiInstance from "../../../utility/apiInstance";
import dayjs from "dayjs";
import CustomSelect from "../../../core/common/commonSelect";
import {
    Country,
    Gender,
} from "../../../core/common/selectOption/json/selectOption";
import { DatePicker } from "antd";
import { ALPHA_REGEX, stripNonAlpha, stripNonNumeric } from "../../../utility/inputValidators";

interface PopupProps {
    show: boolean;
    onClose: () => void;
    addStudentHandler: any;
}

const AddStudentPopup: React.FC<PopupProps> = ({
    show,
    onClose,
    addStudentHandler,
}) => {
    const user = useSelector((state: any) => state.auth.user);
    const [recruiterTermsCond, setRecruiterTermsCond] =
        useState<boolean>(false);

    const getModalContainer = () => {
        const modalElement = document.getElementById("add-student-pp");
        return modalElement ? modalElement : document.body;
    };

    const [msg, setMsg] = useState({
        error: false,
        loading: false,
        text: "",
    });

    const [errorField, setErrorField] = useState('');

    const [formData, setFormData] = useState<User | null>();

    useEffect(() => {
        setFormData({
            firstName: "",
            lastName: "",
            dob: new Date(),
            countryOfCitizenship: "",
            firstLanguage: "",
            passport: "",
            passportExpiry: "",
            gender: "",
            email: "",
            phone: "",
        });

        setRecruiterTermsCond(false)
    }, [show]);

    useEffect(() => {
        if (show) {
            setMsg({ error: false, loading: false, text: "" });
        }
    }, [show]);

    const handleInputChange = (e: any) => {
        const { name } = e.target;
        let { value } = e.target;
        if (typeof value === 'string' && (name === 'firstName' || name === 'lastName')) {
            value = stripNonAlpha(value);
        } else if (typeof value === 'string' && name === 'phone') {
            value = stripNonNumeric(value);
        }

        setFormData((prevState) => {
            return prevState
                ? {
                      ...prevState,
                      [name]: value,
                  }
                : null;
        });
        if (errorField === name) setErrorField('');
    };

    function generateRandomPassword(length = 12) {
        const charset =
            "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+~";
        let password = "";
        for (let i = 0; i < length; i++) {
            password += charset.charAt(
                Math.floor(Math.random() * charset.length)
            );
        }
        return password;
    }
    
    const validate = () => {

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const phoneRegex = /^[0-9]{7,15}$/;

        let newErrors = "";
        let invalidField = "";

        if (!formData?.firstName?.trim() || !ALPHA_REGEX.test(formData.firstName)) {
            newErrors = "Valid first name is required";
            invalidField = 'firstName';
        } else if (!formData?.lastName?.trim() || !ALPHA_REGEX.test(formData.lastName)) {
            newErrors = "Valid last name is required";
            invalidField = 'lastName';
        } else if (!formData?.dob || isNaN(new Date(formData.dob).getTime())) {
            newErrors = "Valid date of birth is required";
            invalidField = 'dob';
        } else if (!formData?.gender?.trim()) {
            newErrors = "Gender is required";
            invalidField = 'gender';
        } else if (!formData?.countryOfCitizenship?.trim()) {
            newErrors = "Country of citizenship is required";
            invalidField = 'countryOfCitizenship';
        } else if (!formData?.firstLanguage?.trim()) {
            newErrors = "First language is required";
            invalidField = 'firstLanguage';
        } else if (!formData?.passport?.trim()) {
            newErrors = "Passport number is required";
            invalidField = 'passport';
        } else if (
            !formData?.passportExpiry?.trim() ||
            isNaN(new Date(formData.passportExpiry).getTime())
        ) {
            newErrors = "Valid passport expiry date is required";
            invalidField = 'passportExpiry';
        } else if (!formData?.email?.trim() || !emailRegex?.test(formData.email)) {
            newErrors = "Valid email is required";
            invalidField = 'email';
        } else if (!formData?.phone?.trim() || !phoneRegex.test(formData.phone)) {
            newErrors = "Valid phone number is required";
            invalidField = 'phone';
        }

        setErrorField(invalidField);

        setMsg({
            error: true,
            loading: false,
            text: newErrors,
        });

        return newErrors.length === 0;
    };

    const saveStudent = async () => {

        if (!validate()) return;

        if (!recruiterTermsCond) {
            setMsg({
                error: true,
                loading: false,
                text: "Please confirm the student consent",
            });
            return
        }

        setMsg({
            error: false,
            loading: true,
            text: "",
        });

        try {
            const res = await apiInstance.post(`/api/users`, {
                ...formData,
                role: "student",
                agent: user.id,
                password: generateRandomPassword()
            });
            addStudentHandler(res.data.doc);

            setMsg({
                error: false,
                loading: false,
                text: "Student added successfully!",
            });
            setTimeout(onClose, 3000);
        } catch (err: any) {
            setMsg({
                error: true,
                loading: false,
                text: err.message,
            });
        }
    };

    return (
        <div
            className={`modal fade ${show ? "show d-block" : ""}`}
            tabIndex={-1}
            role="dialog"
            style={{ background: show ? "rgba(0,0,0,0.5)" : "transparent" }}
            id="add-student-pp"
        >
            <div className="modal-dialog-centered w-50 m-auto" role="document">
                <div className="modal-content">
                    {/* Header */}
                    <div className="modal-header">
                        <h5 className="modal-title text-secondary">
                            Add Student
                        </h5>
                        <button
                            type="button"
                            className="btn-close"
                            onClick={onClose}
                        ></button>
                    </div>

                    {/* Body */}
                    <div className="modal-body">
                        <h5 className="fw-bold mb-5 text-center">
                            {formData?.firstName}
                        </h5>

                        <div className="card mb-4 shadow-sm border-0 rounded-3">
                            <div className="card-body">
                                <form>
                                    <div className="row">
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label className="form-label">
                                                    First Name{" "}
                                                    <span className="text-danger">
                                                        {" "}
                                                        *
                                                    </span>
                                                </label>
                                                <input
                                                    type="text"
                                                    className={`form-control ${errorField === 'firstName' ? 'is-invalid' : ''}`}
                                                    name="firstName"
                                                    value={
                                                        formData?.firstName ||
                                                        ""
                                                    }
                                                    onChange={handleInputChange}
                                                />
                                                {errorField === 'firstName' && (
                                                    <div className="invalid-feedback d-block">{msg.text}</div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label className="form-label">
                                                    Last Name{" "}
                                                    <span className="text-danger">
                                                        {" "}
                                                        *
                                                    </span>
                                                </label>
                                                <input
                                                    type="text"
                                                    className={`form-control ${errorField === 'lastName' ? 'is-invalid' : ''}`}
                                                    name="lastName"
                                                    value={
                                                        formData?.lastName || ""
                                                    }
                                                    onChange={handleInputChange}
                                                />
                                                {errorField === 'lastName' && (
                                                    <div className="invalid-feedback d-block">{msg.text}</div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label className="form-label">
                                                    DOB{" "}
                                                    <span className="text-danger">
                                                        {" "}
                                                        *
                                                    </span>
                                                </label>
                                                <div className="input-icon-start position-relative">
                                                    <span className="input-icon-addon">
                                                        <i className="isax isax-calendar" />
                                                    </span>

                                                    <DatePicker
                                                        name="dob"
                                                        className={`form-control datetimepicker ${errorField === 'dob' ? 'is-invalid' : ''}`}
                                                        getPopupContainer={
                                                            getModalContainer
                                                        }
                                                        placeholder="Select Date"
                                                        format="DD/MM/YYYY"
                                                        onChange={(
                                                            date
                                                        ) => {
                                                            handleInputChange({
                                                                target: {
                                                                    name: "dob",
                                                                    value: date ? date.format('YYYY-MM-DD') : '',
                                                                },
                                                            });
                                                        }}
                                                        value={
                                                            formData?.dob
                                                                ? dayjs(
                                                                      formData.dob
                                                                  )
                                                                : null
                                                        }
                                                    />
                                                </div>
                                                {errorField === 'dob' && (
                                                    <div className="invalid-feedback d-block">{msg.text}</div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label className="form-label">
                                                    Gender{" "}
                                                    <span className="text-danger">
                                                        {" "}
                                                        *
                                                    </span>
                                                </label>
                                                <CustomSelect
                                                    options={Gender}
                                                    className={`select d-flex ${errorField === 'gender' ? 'is-invalid' : ''}`}
                                                    placeholder="Select Gender"
                                                    name="gender"
                                                    value={formData?.gender}
                                                    onChange={(
                                                        selectedOption
                                                    ) => {
                                                        handleInputChange({
                                                            target: {
                                                                name: "gender",
                                                                value: selectedOption.value,
                                                            },
                                                        });
                                                    }}
                                                />
                                                {errorField === 'gender' && (
                                                    <div className="invalid-feedback d-block">{msg.text}</div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label className="form-label">
                                                    Country of Citizenship{" "}
                                                    <span className="text-danger">
                                                        {" "}
                                                        *
                                                    </span>
                                                </label>
                                                <CustomSelect
                                                    options={Country}
                                                    className={`select d-flex ${errorField === 'countryOfCitizenship' ? 'is-invalid' : ''}`}
                                                    placeholder="Select Country of Citizenship"
                                                    name="countryOfCitizenship"
                                                    value={
                                                        formData?.countryOfCitizenship
                                                    }
                                                    onChange={(
                                                        selectedOption
                                                    ) => {
                                                        handleInputChange({
                                                            target: {
                                                                name: "countryOfCitizenship",
                                                                value: selectedOption.value,
                                                            },
                                                        });
                                                    }}
                                                />
                                                {errorField === 'countryOfCitizenship' && (
                                                    <div className="invalid-feedback d-block">{msg.text}</div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label className="form-label">
                                                    First Language{" "}
                                                    <span className="text-danger">
                                                        {" "}
                                                        *
                                                    </span>
                                                </label>
                                                <input
                                                    type="text"
                                                    className={`form-control ${errorField === 'firstLanguage' ? 'is-invalid' : ''}`}
                                                    defaultValue=""
                                                    name="firstLanguage"
                                                    value={
                                                        formData?.firstLanguage
                                                    }
                                                    onChange={handleInputChange}
                                                />
                                                {errorField === 'firstLanguage' && (
                                                    <div className="invalid-feedback d-block">{msg.text}</div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label className="form-label">
                                                    Passport{" "}
                                                    <span className="text-danger">
                                                        {" "}
                                                        *
                                                    </span>
                                                </label>
                                                <input
                                                    type="text"
                                                    className={`form-control ${errorField === 'passport' ? 'is-invalid' : ''}`}
                                                    defaultValue=""
                                                    name="passport"
                                                    value={formData?.passport}
                                                    onChange={handleInputChange}
                                                />
                                                {errorField === 'passport' && (
                                                    <div className="invalid-feedback d-block">{msg.text}</div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label className="form-label">
                                                    Passport Expiry{" "}
                                                    <span className="text-danger">
                                                        {" "}
                                                        *
                                                    </span>
                                                </label>
                                                <div className="input-icon-start position-relative">
                                                    <span className="input-icon-addon">
                                                        <i className="isax isax-calendar" />
                                                    </span>

                                                    <DatePicker
                                                        name="passportExpiry"
                                                        className={`form-control datetimepicker ${errorField === 'passportExpiry' ? 'is-invalid' : ''}`}
                                                        getPopupContainer={
                                                            getModalContainer
                                                        }
                                                        placeholder="Select Date"
                                                        format="DD/MM/YYYY"
                                                        onChange={(
                                                            date
                                                        ) => {
                                                            handleInputChange({
                                                                target: {
                                                                    name: "passportExpiry",
                                                                    value: date ? date.format('YYYY-MM-DD') : '',
                                                                },
                                                            });
                                                        }}
                                                        value={
                                                            formData?.passportExpiry
                                                                ? dayjs(
                                                                      formData.passportExpiry
                                                                  )
                                                                : null
                                                        }
                                                    />
                                                </div>
                                                {errorField === 'passportExpiry' && (
                                                    <div className="invalid-feedback d-block">{msg.text}</div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label className="form-label">
                                                    Email{" "}
                                                    <span className="text-danger">
                                                        {" "}
                                                        *
                                                    </span>
                                                </label>
                                                <input
                                                    type="email"
                                                    className={`form-control ${errorField === 'email' ? 'is-invalid' : ''}`}
                                                    defaultValue=""
                                                    name="email"
                                                    value={formData?.email}
                                                    onChange={handleInputChange}
                                                />
                                                {errorField === 'email' && (
                                                    <div className="invalid-feedback d-block">{msg.text}</div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label className="form-label">
                                                    Phone Number{" "}
                                                    <span className="text-danger">
                                                        {" "}
                                                        *
                                                    </span>
                                                </label>
                                                <input
                                                    type="text"
                                                    className={`form-control ${errorField === 'phone' ? 'is-invalid' : ''}`}
                                                    defaultValue=""
                                                    name="phone"
                                                    value={formData?.phone}
                                                    onChange={handleInputChange}
                                                />
                                                {errorField === 'phone' && (
                                                    <div className="invalid-feedback d-block">{msg.text}</div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="d-flex align-items-center justify-content-between mb-4">
                                            <div className="remember-me d-flex align-items-center gap-3">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    defaultValue=""
                                                    id="flexCheckDefaultRecruiter"
                                                    checked={recruiterTermsCond}
                                                    onChange={(e) => {
                                                        setRecruiterTermsCond(
                                                            (prev) => !prev
                                                        );
                                                    }}
                                                />
                                                <label
                                                    className="form-check-label mb-0 d-inline-flex remember-me fs-14"
                                                    htmlFor="flexCheckDefaultRecruiter"
                                                >
                                                    I confirm that I have
                                                    received express written
                                                    consent from the student
                                                    whom I am creating this
                                                    profile for and I can
                                                    provide proof of their
                                                    consent upon request.
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="modal-footer d-flex gap-3">
                        {msg.loading && (
                            <span className="mt-3 fs-14 fw-normal d-flex align-items-center justify-content-center">
                                Saving student...
                            </span>
                        )}
                        {!msg.loading && !errorField && (
                            <span
                                className={`mt-3 ${msg.error ? "text-red" : ""} fs-14 fw-normal d-flex align-items-center justify-content-center`}
                            >
                                {msg.text}
                            </span>
                        )}
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={saveStudent}
                        >
                            Save
                        </button>
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={onClose}
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddStudentPopup;
